import React, { FC } from 'react';

import { Grid, Radio, Typography } from '@mui/material';
import { Box } from '@mui/system';

import { PaymentPlans, pharmacyBillingSelector } from '@pharmaplan/common';

import { useAppSelector } from '../../../../../../../hooks/useAppSelector';
import ThemeConstants from '../../../../../../../theme/ThemeConstants';
import strings from '../../../../../../../localization';

import styles from './styles';

const { basic, unlimited } = PaymentPlans;

interface IPlanBlockWithRadio {
  planType: PaymentPlans;
  showRadio: true;
  setPaymentPlan: React.Dispatch<React.SetStateAction<PaymentPlans>>;
  selectedPlan: PaymentPlans;
}

interface IPlanBlockWithouRadio {
  planType: PaymentPlans;
  showRadio: false;
  setPaymentPlan?: React.Dispatch<React.SetStateAction<PaymentPlans>>;
  selectedPlan?: PaymentPlans;
}

type IPlanBlock = IPlanBlockWithRadio | IPlanBlockWithouRadio;

const PlanBlock: FC<IPlanBlock> = ({
  planType,
  setPaymentPlan,
  selectedPlan,
  showRadio,
}) => {
  const billing = useAppSelector(pharmacyBillingSelector);
  const { unlimitedFees, basicFees } = billing ?? {};

  const config = {
    [basic]: {
      key: PaymentPlans.basic,
      label: strings.idealForOccassionalUser,
      plan: strings.formatString(strings.planFormat, strings.standard),
      per: strings.contract,
      planCost: `$${basicFees}`,
      caption: strings.flatFeeForEachContract,
      color: ThemeConstants.payment.orange,
    },
    [unlimited]: {
      key: PaymentPlans.unlimited,
      label: strings.idealForFrequentUser,
      plan: strings.formatString(strings.planFormat, strings.unlimited),
      per: strings.month.toLowerCase(),
      planCost: `$${unlimitedFees}`,
      caption: strings.unlimitedContractWithoutFees,
      color: ThemeConstants.payment.elegantGreen,
    },
  };

  const { key, label, plan, planCost, caption, color, per } = config[planType];

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPaymentPlan?.(event.target.value as PaymentPlans);
  };

  return (
    <Grid sx={styles.planContainer} key={key} item>
      <Box>
        <Box
          sx={[styles.componentMargin, styles.chip, { backgroundColor: color }]}
        >
          <Typography sx={styles.chipLabel}>{label}</Typography>
        </Box>

        <Typography sx={[styles.planType]}>{plan}</Typography>

        <Typography sx={[styles.planCost, styles.componentMargin]}>
          {planCost}
          <Typography sx={styles.subLabel} component="span">
            {`/${per}`}
          </Typography>
        </Typography>

        <Typography sx={[styles.subLabel, styles.caption]}>
          {caption}
        </Typography>
      </Box>
      {showRadio && (
        <Box>
          <Radio
            onChange={handleChange}
            value={key}
            checked={key === selectedPlan}
            sx={styles.radio}
            name="radio-buttons"
            inputProps={{ 'aria-label': key }}
          />
        </Box>
      )}
    </Grid>
  );
};

export default PlanBlock;
